import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './EnhancedProgressBar.css';
import { AchievementDefinition } from '../../games/services/GameAPI';

interface EnhancedProgressBarProps {
  current: number;
  target: number;
  missionId: string;
  label: string;
  unit: string;
  className?: string;
  isActiveClass?: boolean;
  achievements?: AchievementDefinition[];
  gameId?: string;
}

export const EnhancedProgressBar: React.FC<EnhancedProgressBarProps> = ({
  current,
  target,
  missionId,
  label,
  unit,
  className = '',
  isActiveClass = true,
  achievements = [],
  gameId
}) => {
  const [previousValue, setPreviousValue] = useState(current);
  const [showCelebration, setShowCelebration] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);
  const lastUpdateRef = useRef<number>(0);
  
  // Performance optimizations for chromebooks
  const percentage = isActiveClass ? Math.min((current / target) * 100, 100) : 0;
  const isComplete = isActiveClass && current >= target;
  const isOverflow = isActiveClass && current > target;
  const overflowLevel = isOverflow ? Math.floor((current - target) / target * 100 / 25) : 0;

  // Intersection Observer for performance - only animate visible bars
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (progressRef.current) {
      observer.observe(progressRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Enhanced celebration logic - optimized for new bouncy animations
  const triggerCelebration = useCallback((message: string, isOverflowCelebration: boolean = false) => {
    const now = Date.now();
    
    // Throttle celebrations to max 1 per 2 seconds
    if (now - lastUpdateRef.current < 2000) return;
    
    lastUpdateRef.current = now;
    setShowCelebration(message);
    
    // Different durations for different celebration types
    const duration = isOverflowCelebration ? 2000 : 2500;
    setTimeout(() => setShowCelebration(null), duration);
  }, []);

  // Celebration logic with performance considerations
  useEffect(() => {
    const wasComplete = previousValue >= target;
    const isNowComplete = current >= target;
    const wasOverflow = previousValue > target;
    const isNowOverflow = current > target;

    // Only trigger if visible and class is active to save CPU
    if (!isVisible || !isActiveClass) {
      setPreviousValue(current);
      return;
    }

    // First time completing (100%)
    if (!wasComplete && isNowComplete) {
      triggerCelebration('🎉 Incredible! 🎉', false);
    }
    
    // Overflow milestones (every 25% beyond target)
    else if (isNowOverflow) {
      const prevOverflowLevel = Math.floor(Math.max(0, previousValue - target) / target * 100 / 25);
      const newOverflowLevel = Math.floor((current - target) / target * 100 / 25);
      
      if (newOverflowLevel > prevOverflowLevel) {
        const messages = [
          '🔥 On fire! 🔥',
          '⚡ Unstoppable! ⚡',
          '🚀 Amazing! 🚀',
          '🏆 Legendary! 🏆'
        ];
        const message = messages[Math.min(newOverflowLevel - 1, messages.length - 1)];
        triggerCelebration(message, true);
      }
    }

    setPreviousValue(current);
  }, [current, target, previousValue, isVisible, triggerCelebration]);

  // Performance-optimized stage calculation
  const getProgressStage = useCallback(() => {
    if (isOverflow) return 'overflow';
    if (isComplete) return 'completed';
    if (percentage >= 90) return 'stage-90';
    if (percentage >= 75) return 'stage-75';
    if (percentage >= 50) return 'stage-50';
    return '';
  }, [isOverflow, isComplete, percentage]);

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [selectedAchievement, setSelectedAchievement] = useState<AchievementDefinition | null>(null);
  const [tooltipSide, setTooltipSide] = useState<'left' | 'right'>('right');

  const tooltipShowTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tooltipHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);

  const updateTooltipPosition = useCallback((element: HTMLDivElement) => {
    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const tooltipWidth = 380;
    const offset = 12; // Gap between progress bar and tooltip

    let left = rect.right + offset;
    let top = rect.top + rect.height / 2;
    let side: 'left' | 'right' = 'right';

    if (left + tooltipWidth > viewportWidth - 10) {
      left = rect.left - tooltipWidth - offset;
      side = 'left';
    }

    if (isMountedRef.current) {
      setTooltipPosition({ top, left });
      setTooltipSide(side);
    }
  }, []);

  const handleMouseEnter = () => {
    if (tooltipHideTimeoutRef.current) {
      clearTimeout(tooltipHideTimeoutRef.current);
    }
    tooltipShowTimeoutRef.current = setTimeout(() => {
      const element = progressBarRef.current;
      if (!element || !isMountedRef.current) return;
      
      updateTooltipPosition(element);
      setShowTooltip(true);
    }, 200);
  };

  const handleMouseLeave = () => {
    if (tooltipShowTimeoutRef.current) {
      clearTimeout(tooltipShowTimeoutRef.current);
    }
    tooltipHideTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setShowTooltip(false);
        setSelectedAchievement(null);
      }
    }, 200);
  };
  
  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (tooltipShowTimeoutRef.current) clearTimeout(tooltipShowTimeoutRef.current);
      if (tooltipHideTimeoutRef.current) clearTimeout(tooltipHideTimeoutRef.current);
    };
  }, []);

  return (
    <div className={`enhanced-progress-container ${className}`} ref={progressRef}>
      <div className="mission-text-header">
        <span className="mission-text">{label}</span>
        <span className="mission-counter">
          {isActiveClass ? `${current}/${target}` : '?/?'} {unit}
        </span>
      </div>
      
      <div 
        className="enhanced-progress-bar" 
        ref={progressBarRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div 
          className={`enhanced-progress-fill ${getProgressStage()}`}
          style={{ 
            '--progress-width': `${percentage}%`,
            '--overflow-width': isOverflow ? `${Math.min(percentage + overflowLevel * 10, 150)}%` : `${percentage}%`
          } as React.CSSProperties}
        />
        
        {/* Enhanced external celebration system */}
        {showCelebration && isVisible && (
          <div className={`celebration-popup ${isOverflow ? 'overflow' : ''}`}>
            {showCelebration}
          </div>
        )}
        
        {/* Overflow spark effects - only render if overflowing and visible */}
        {isOverflow && isVisible && (
          <div className="overflow-sparks">
            <div className="spark"></div>
            <div className="spark"></div>
            <div className="spark"></div>
            <div className="spark"></div>
            <div className="spark"></div>
          </div>
        )}
      </div>
      
            {/* Custom Achievement Tooltip Portal */}
      {showTooltip && achievements.length > 0 && createPortal(
        <div 
          className="achievement-tooltip"
          style={{
            position: 'fixed',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            zIndex: 99999, // Even higher z-index
            pointerEvents: 'auto',
            isolation: 'isolate', // Create new stacking context
            transform: 'translateY(-50%)' // Vertically center
          }}
          onMouseEnter={() => {
            if (tooltipHideTimeoutRef.current) {
              clearTimeout(tooltipHideTimeoutRef.current);
            }
          }}
          onMouseLeave={() => {
            if (isMountedRef.current) {
              setShowTooltip(false);
              setSelectedAchievement(null);
            }
          }}
        >
          <div className="tooltip-header">
            <h4>{achievements.length} Achievements to Unlock</h4>
          </div>
          <div className="tooltip-achievements">
            {achievements.map((achievement) => (
              <div 
                key={achievement.id} 
                className={`tooltip-achievement ${selectedAchievement?.id === achievement.id ? 'selected' : ''}`}
                onClick={() => setSelectedAchievement(selectedAchievement?.id === achievement.id ? null : achievement)}
              >
                <span className="achievement-icon">{achievement.iconUrl}</span>
                <div className="achievement-info">
                  <div className="achievement-title">{achievement.title}</div>
                  {selectedAchievement?.id === achievement.id && (
                    <div className="achievement-description">
                      {achievement.description}
                      <div className="achievement-reward">
                        Reward: {achievement.museCashReward} 💰
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className={`tooltip-arrow tooltip-arrow-${tooltipSide}`}></div>
        </div>,
        document.body
      )}
    </div>
  );
}; 